import React, { useEffect, useState } from 'react';
import { formatMoney } from '../../utils/money';

function MerxandatgeOrderTab({ items, socket, isAdmin, itemImages }) {
  const [localSortedItems, setLocalSortedItems] = useState(null);

  // Get filtered items based on user role and visibility preferences
  const getFilteredItems = () => {
    if (!items) return [];

    // If user is not admin, only show active items
    if (!isAdmin) {
      return items.filter(item => item.active);
    }

    // If user is admin, show all items (we'll handle visibility in parent component)
    return items;
  };

  // Sorting rules used both for initial compute and reconciliation
  const sortByRules = (list) => {
    return [...list].sort((a, b) => {
      const aHasOrder = a.display_order !== undefined && a.display_order !== null;
      const bHasOrder = b.display_order !== undefined && b.display_order !== null;

      // If both have display_order, respect it (admin-defined order)
      if (aHasOrder && bHasOrder) {
        return a.display_order - b.display_order;
      }

      // Items with display_order come before those without
      if (aHasOrder && !bHasOrder) return -1;
      if (!aHasOrder && bHasOrder) return 1;

      // Default: deadline items first, then by id
      const aHasDeadline = a.type === 'made_to_order' && a.next_round_deadline;
      const bHasDeadline = b.type === 'made_to_order' && b.next_round_deadline;

      if (aHasDeadline && !bHasDeadline) return -1;
      if (!aHasDeadline && bHasDeadline) return 1;

      if (aHasDeadline && bHasDeadline) {
        return new Date(a.next_round_deadline) - new Date(b.next_round_deadline);
      }

      return b.id - a.id;
    });
  };

  // Keep a local copy for instant UI feedback; reconcile when props change
  useEffect(() => {
    const filtered = getFilteredItems();
    const sortedFromProps = sortByRules(filtered);

    if (!localSortedItems) {
      setLocalSortedItems(sortedFromProps);
      return;
    }

    // Preserve current local order where possible, refresh item objects, append new ones
    const idToFreshItem = new Map(sortedFromProps.map((it) => [it.id, it]));
    let updated = localSortedItems
      .filter((it) => idToFreshItem.has(it.id))
      .map((it) => idToFreshItem.get(it.id));

    const existingIds = new Set(updated.map((it) => it.id));
    const toAppend = sortedFromProps.filter((it) => !existingIds.has(it.id));

    if (toAppend.length > 0 || updated.length !== localSortedItems.length) {
      updated = [...updated, ...toAppend];
    }

    setLocalSortedItems(updated);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, isAdmin]);

  // Prefer local order when available
  const getSortedItems = () => {
    if (localSortedItems) return localSortedItems;
    const filteredItems = getFilteredItems();
    return sortByRules(filteredItems);
  };

  const handleMoveUp = (itemId) => {
    const sortedItems = getSortedItems();
    const currentIndex = sortedItems.findIndex(item => item.id === itemId);

    if (currentIndex > 0) {
      const newOrder = [...sortedItems];
      [newOrder[currentIndex], newOrder[currentIndex - 1]] = [newOrder[currentIndex - 1], newOrder[currentIndex]];

      setLocalSortedItems(newOrder);

      const orderData = newOrder.map((item, index) => ({ id: item.id, display_order: index }));
      socket.emit('.update_merxandatge_order', orderData);
    }
  };

  const handleMoveDown = (itemId) => {
    const sortedItems = getSortedItems();
    const currentIndex = sortedItems.findIndex(item => item.id === itemId);

    if (currentIndex < sortedItems.length - 1) {
      const newOrder = [...sortedItems];
      [newOrder[currentIndex], newOrder[currentIndex + 1]] = [newOrder[currentIndex + 1], newOrder[currentIndex]];

      setLocalSortedItems(newOrder);

      const orderData = newOrder.map((item, index) => ({ id: item.id, display_order: index }));
      socket.emit('.update_merxandatge_order', orderData);
    }
  };

  const getItemImageUrl = (item) => {
    return itemImages[item.id] || item.image_url || '/default-merchandise.png';
  };

  const renderOrderTab = () => {
    const sortedItems = getSortedItems();

    return (
      <div className="order-tab-container">
        <div className="order-controls">
          <h3>Ordena els productes</h3>
          <p>Els productes sota comanda amb data límit apareixen primer per defecte. Pots reordenar tots els productes lliurement:</p>
        </div>

        <div className="order-items-list">
          {sortedItems.map((item, index) => {
            const hasDeadline = item.type === 'made_to_order' && item.next_round_deadline;
            const isExpired = hasDeadline && new Date(item.next_round_deadline) < new Date();
            const deadlineText = hasDeadline ?
              `Fins: ${new Date(item.next_round_deadline).toLocaleDateString('ca-ES')}` : '';

            return (
              <div key={item.id} className={`order-item ${hasDeadline ? (isExpired ? 'expired-item' : 'urgent-item') : ''} ${item.active ? '' : 'inactive-row'}`}>
                <div className="item-info">
                  <div className="item-position">{index + 1}</div>
                  <img src={getItemImageUrl(item)} alt={item.name} className="item-thumbnail-small" />
                  <div className="item-details-small">
                    <div className="item-name-small">
                      {item.name}
                      {item.active ? null : <span className="inactive-tag-inline">No visible</span>}
                    </div>
                    <div className="item-price-small">{formatMoney(item.price)}€</div>
                    {deadlineText ? <div className="item-deadline">{deadlineText}</div> : null}
                  </div>
                </div>

                <div className="item-controls">
                  {hasDeadline ? (
                    <div className={`urgent-badge ${isExpired ? 'expired-badge' : ''}`}>
                      {isExpired ? 'Fora de termini' : 'Amb data límit'}
                    </div>
                  ) : null}
                  <button
                    className="move-button"
                    onClick={() => handleMoveUp(item.id)}
                    disabled={index === 0}
                  >
                    ↑
                  </button>
                  <button
                    className="move-button"
                    onClick={() => handleMoveDown(item.id)}
                    disabled={index === sortedItems.length - 1}
                  >
                    ↓
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return renderOrderTab();
}

export default MerxandatgeOrderTab;
